import { useCallback } from 'react'
import {
  DiscountFormV2StateType,
  StandardPrerequisite,
  PrerequisiteType,
  PrerequisiteCatalogueType,
  PrerequisiteCatalogueEntityType,
  EntitledCatalogueEntityType,
  DiscountFieldPaths,
  PurchaseConditionAmountType,
  PurchaseConditionType
} from 'src/lib/types/discountsV2'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'

interface UsePrerequisiteProductsProps {
  prerequisite: StandardPrerequisite
  setFormState: (payload: Partial<DiscountFormV2StateType>, clearPaths?: string[]) => void
  catalogueType?: 'COLLECTION' | 'PRODUCT_AND_VARIANT'
}

export const usePrerequisiteProducts = ({
  prerequisite,
  setFormState,
  catalogueType
}: UsePrerequisiteProductsProps) => {
  const { prerequisiteCatalogue, metaData } = prerequisite
  const items = metaData?.prerequisiteCatalogue || []

  const calculateTotalQuantity = useCallback((catalogue: PrerequisiteCatalogueType[]) => {
    return catalogue.reduce((sum: number, group: any) => sum + (group.quantity || 0), 0)
  }, [])

  const updateAndClearErrors = useCallback(
    (payload: Partial<DiscountFormV2StateType>, groupIndex: number, newCount: number, oldCount: number) => {
      const maxEntityCount = Math.max(newCount, oldCount)
      const entityPaths = Array.from(
        { length: maxEntityCount },
        (_, i) => `${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.entities.${i}`
      )

      setFormState(payload, [
        DiscountFieldPaths.PREREQUISITE_CATALOGUE,
        `${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${groupIndex}.entities`,
        ...entityPaths
      ])
    },
    [setFormState]
  )

  const getNewEntity = (
    type: PrerequisiteType,
    quantity: number,
    price: number,
    entities: { type: PrerequisiteCatalogueEntityType; entityId: string[] }[]
  ): PrerequisiteCatalogueType => {
    if (type === PrerequisiteType.QUANTITY) {
      return { type: PrerequisiteType.QUANTITY, quantity, entities }
    }
    return { type: PrerequisiteType.PRICE, price, entities }
  }

  const handleItemsSelected = useCallback(
    (products: CatalogueDataType[], excludedProducts: CatalogueDataType[] = [], groupIndex: number) => {
      const currentCatalogueType =
        catalogueType ||
        (prerequisite?.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
          ? 'COLLECTION'
          : 'PRODUCT_AND_VARIANT')

      const mapToEntity = (product: CatalogueDataType) => {
        const isAllSelector = product.id === 'shopflo-all-products' || product.id === 'shopflo-all-collections'

        if (currentCatalogueType === 'COLLECTION') {
          return {
            type: PrerequisiteCatalogueEntityType.COLLECTION,
            entityId: isAllSelector ? [] : [product.id || '']
          }
        }

        const selectedVariants = product.variants?.filter(Boolean) || []
        const totalVariants = product.totalVariantCount || 0
        const isPartial = selectedVariants.length > 0 && selectedVariants.length < totalVariants

        if (isPartial) {
          return {
            type: PrerequisiteCatalogueEntityType.VARIANT,
            entityId: selectedVariants.map(variant => variant.id || '')
          }
        }

        return {
          type: PrerequisiteCatalogueEntityType.PRODUCT,
          entityId: isAllSelector ? [] : [product.id || '']
        }
      }

      const mapToExcludedEntity = (product: CatalogueDataType) => {
        const isAllSelector = product.id === 'shopflo-all-products' || product.id === 'shopflo-all-collections'

        if (currentCatalogueType === 'COLLECTION') {
          return {
            type: EntitledCatalogueEntityType.COLLECTION,
            entityId: isAllSelector ? [] : [product.id || '']
          }
        }

        const selectedVariants = product.variants?.filter(Boolean) || []
        const totalVariants = product.totalVariantCount || 0
        const isPartial = selectedVariants.length > 0 && selectedVariants.length < totalVariants

        if (isPartial) {
          return {
            type: EntitledCatalogueEntityType.VARIANT,
            entityId: selectedVariants.map(variant => variant.id || '')
          }
        }

        return {
          type: EntitledCatalogueEntityType.PRODUCT,
          entityId: isAllSelector ? [] : [product.id || '']
        }
      }

      const nextEntities = products.map(mapToEntity)
      const nextExcludedEntities = excludedProducts.map(mapToExcludedEntity)

      const existingGroup = prerequisiteCatalogue[groupIndex]
      const isAmountMode = prerequisite?.purchaseConditionType === PurchaseConditionType.CART_AMOUNT

      let type = isAmountMode ? PrerequisiteType.PRICE : PrerequisiteType.QUANTITY
      let quantity = 1
      let price = 0

      if (existingGroup) {
        type = existingGroup.type
        if (existingGroup.type === PrerequisiteType.QUANTITY) quantity = existingGroup.quantity || 1
        if (existingGroup.type === PrerequisiteType.PRICE) price = existingGroup.price || 0
      } else if (!prerequisite && prerequisiteCatalogue?.[0]) {
        type = prerequisiteCatalogue[0].type
      }

      const newPrerequisiteEntity = getNewEntity(type, quantity, price, nextEntities)

      // 3. Update State Arrays
      const nextPrerequisites = [...prerequisiteCatalogue]
      const nextMetaCatalogue = [...items]

      if (existingGroup) {
        nextPrerequisites[groupIndex] = {
          ...existingGroup,
          entities: newPrerequisiteEntity.entities
        }
      } else {
        nextPrerequisites[groupIndex] = newPrerequisiteEntity
      }
      nextMetaCatalogue[groupIndex] = products

      const nextExcludedCatalogue = [...(prerequisite.excludedCatalogue || [])]
      const nextExcludedMetaData = [...(prerequisite.metaData?.excludedCatalogue || [])]
      nextExcludedCatalogue[groupIndex] = { entities: nextExcludedEntities }
      nextExcludedMetaData[groupIndex] = excludedProducts

      const totalQuantity = calculateTotalQuantity(nextPrerequisites)
      const isPriceType = prerequisite.prerequisiteType === PrerequisiteType.PRICE

      const commonUpdates = {
        prerequisiteCatalogue: nextPrerequisites,
        excludedCatalogue: nextExcludedCatalogue,
        metaData: {
          ...prerequisite.metaData,
          prerequisiteCatalogue: nextMetaCatalogue,
          excludedCatalogue: nextExcludedMetaData
        }
      }

      updateAndClearErrors(
        {
          prerequisite: {
            ...prerequisite,
            ...commonUpdates,
            ...(!isPriceType && { prerequisiteCountToEntitlement: totalQuantity })
          }
        },
        groupIndex,
        newPrerequisiteEntity.entities.length,
        existingGroup?.entities.length || 0
      )
    },
    [prerequisiteCatalogue, items, catalogueType, setFormState, prerequisite, calculateTotalQuantity]
  )

  const handleRemoveExcludedItem = useCallback(
    (groupIndex: number, itemIndex: number) => {
      const includedLength = prerequisiteCatalogue[groupIndex]?.entities?.length ?? 0
      const excludedIndex = itemIndex - includedLength
      if (excludedIndex < 0) return

      const nextExcludedCatalogue = [...(prerequisite.excludedCatalogue || [])]
      const nextExcludedMetaData = [...(prerequisite.metaData?.excludedCatalogue || [])]

      const targetGroup = nextExcludedCatalogue[groupIndex]

      if (targetGroup && !Array.isArray(targetGroup)) {
        const updatedGroup = { ...targetGroup }
        updatedGroup.entities = [...updatedGroup.entities]
        updatedGroup.entities.splice(excludedIndex, 1)
        nextExcludedCatalogue[groupIndex] = updatedGroup
      }

      if (nextExcludedMetaData[groupIndex]) {
        const targetMetaGroup = [...(nextExcludedMetaData[groupIndex] || [])]
        targetMetaGroup.splice(excludedIndex, 1)
        nextExcludedMetaData[groupIndex] = targetMetaGroup
      }

      setFormState({
        prerequisite: {
          ...prerequisite,
          excludedCatalogue: nextExcludedCatalogue,
          metaData: {
            ...prerequisite.metaData,
            excludedCatalogue: nextExcludedMetaData
          }
        }
      })
    },
    [prerequisite, prerequisiteCatalogue, setFormState]
  )

  const handleRemoveGroup = useCallback(
    (index: number) => {
      const newCatalogue = [...prerequisiteCatalogue]
      newCatalogue.splice(index, 1)

      const newMetaDataCatalogue = [...items]
      newMetaDataCatalogue.splice(index, 1)

      const newExcludedCatalogue = [...(prerequisite.excludedCatalogue || [])]
      if (index < newExcludedCatalogue.length) {
        newExcludedCatalogue.splice(index, 1)
      }

      const newExcludedMetaCatalogue = [...(prerequisite.metaData?.excludedCatalogue || [])]
      if (index < newExcludedMetaCatalogue.length) {
        newExcludedMetaCatalogue.splice(index, 1)
      }

      const totalQuantity = calculateTotalQuantity(newCatalogue)

      setFormState(
        {
          prerequisite:
            prerequisite.prerequisiteType === PrerequisiteType.PRICE
              ? {
                  ...prerequisite,
                  prerequisiteCatalogue: newCatalogue,
                  excludedCatalogue: newExcludedCatalogue,
                  metaData: {
                    ...prerequisite.metaData,
                    prerequisiteCatalogue: newMetaDataCatalogue,
                    excludedCatalogue: newExcludedMetaCatalogue
                  }
                }
              : {
                  ...prerequisite,
                  prerequisiteCatalogue: newCatalogue,
                  excludedCatalogue: newExcludedCatalogue,
                  prerequisiteCountToEntitlement: totalQuantity,
                  metaData: {
                    ...prerequisite.metaData,
                    prerequisiteCatalogue: newMetaDataCatalogue,
                    excludedCatalogue: newExcludedMetaCatalogue
                  }
                }
        },
        [DiscountFieldPaths.PREREQUISITE_CATALOGUE]
      )
    },
    [prerequisiteCatalogue, items, setFormState, prerequisite]
  )

  const handleRemoveItemFromGroup = useCallback(
    (groupIndex: number, itemIndex: number) => {
      const newCatalogue = [...prerequisiteCatalogue]
      const group = { ...newCatalogue[groupIndex] }
      group.entities = [...group.entities]
      group.entities.splice(itemIndex, 1)
      newCatalogue[groupIndex] = group

      const newMetaDataCatalogue = [...items]
      const metaGroup = [...newMetaDataCatalogue[groupIndex]]
      metaGroup.splice(itemIndex, 1)
      newMetaDataCatalogue[groupIndex] = metaGroup

      if (group.entities.length === 0) {
        handleRemoveGroup(groupIndex)
      } else {
        const totalQuantity = calculateTotalQuantity(newCatalogue)

        updateAndClearErrors(
          {
            prerequisite:
              prerequisite.prerequisiteType === PrerequisiteType.PRICE
                ? {
                    ...prerequisite,
                    prerequisiteCatalogue: newCatalogue,
                    metaData: {
                      ...prerequisite.metaData,
                      prerequisiteCatalogue: newMetaDataCatalogue
                    }
                  }
                : {
                    ...prerequisite,
                    prerequisiteCatalogue: newCatalogue,
                    prerequisiteCountToEntitlement: totalQuantity,
                    metaData: {
                      ...prerequisite.metaData,
                      prerequisiteCatalogue: newMetaDataCatalogue
                    }
                  }
          },
          groupIndex,
          newCatalogue[groupIndex]?.entities.length || 0,
          group.entities.length
        )
      }
    },
    [prerequisiteCatalogue, items, handleRemoveGroup, setFormState, prerequisite]
  )

  const handleVariantUpdate = useCallback(
    (updatedProduct: CatalogueDataType, groupIndex: number, itemIndex: number) => {
      const newCatalogue = [...prerequisiteCatalogue]
      // Ensure the group exists; if not, return
      if (!newCatalogue[groupIndex]) return

      const group = { ...newCatalogue[groupIndex] }
      group.entities = [...group.entities]

      const selectedVariantIds = updatedProduct.variants?.map(variant => variant.id || '').filter(Boolean) || []
      const totalVariants = updatedProduct.totalVariantCount || 0
      const isPartial = selectedVariantIds.length > 0 && selectedVariantIds.length < totalVariants

      const newType = isPartial ? PrerequisiteCatalogueEntityType.VARIANT : PrerequisiteCatalogueEntityType.PRODUCT
      const newEntityId = isPartial ? selectedVariantIds : [updatedProduct.id || '']

      group.entities[itemIndex] = {
        ...group.entities[itemIndex],
        type: newType,
        entityId: newEntityId
      }

      newCatalogue[groupIndex] = group

      const newMetaDataCatalogue = [...items]
      const metaGroup = [...newMetaDataCatalogue[groupIndex]]

      metaGroup[itemIndex] = {
        ...metaGroup[itemIndex],
        variants: updatedProduct.variants?.map(variant => ({
          id: variant.id || '',
          price: variant.price || 0,
          inventory: variant.inventory || 0,
          isVariant: true,
          description: variant.description || '',
          imageUrl: variant.imageUrl,
          parentDetails: {
            id: updatedProduct.id || '',
            name: updatedProduct.description || ''
          }
        })),
        id: updatedProduct.id || '',
        description: updatedProduct.description || '',
        isVariant: false,
        imageUrl: updatedProduct.imageUrl,
        totalVariantCount: updatedProduct.totalVariantCount
      }
      newMetaDataCatalogue[groupIndex] = metaGroup

      const totalQuantity = calculateTotalQuantity(newCatalogue)

      updateAndClearErrors(
        {
          prerequisite:
            prerequisite.prerequisiteType === PrerequisiteType.PRICE
              ? {
                  ...prerequisite,
                  prerequisiteCatalogue: newCatalogue,
                  metaData: {
                    ...prerequisite.metaData,
                    prerequisiteCatalogue: newMetaDataCatalogue
                  }
                }
              : {
                  ...prerequisite,
                  prerequisiteCatalogue: newCatalogue,
                  prerequisiteCountToEntitlement: totalQuantity,
                  metaData: {
                    ...prerequisite.metaData,
                    prerequisiteCatalogue: newMetaDataCatalogue
                  }
                }
        },
        groupIndex,
        group.entities.length,
        prerequisiteCatalogue[groupIndex]?.entities.length || 0
      )
    },
    [prerequisiteCatalogue, items, setFormState, prerequisite]
  )

  const handleAddEmptyGroup = useCallback(() => {
    let type = PrerequisiteType.QUANTITY
    const quantity = 1
    const price = 1

    type =
      prerequisite.purchaseConditionType === PurchaseConditionType.CART_AMOUNT
        ? PrerequisiteType.PRICE
        : PrerequisiteType.QUANTITY

    const newPrerequisiteEntity = getNewEntity(type, quantity, price, [])
    const newMetaItems: CatalogueDataType[] = []

    const newCatalogue = [...prerequisiteCatalogue, newPrerequisiteEntity]
    const totalQuantity = calculateTotalQuantity(newCatalogue)

    setFormState(
      {
        prerequisite:
          prerequisite.prerequisiteType === PrerequisiteType.PRICE
            ? {
                ...prerequisite,
                prerequisiteCatalogue: newCatalogue,
                metaData: {
                  ...prerequisite.metaData,
                  prerequisiteCatalogue: [...items, newMetaItems]
                }
              }
            : {
                ...prerequisite,
                prerequisiteCatalogue: newCatalogue,
                prerequisiteCountToEntitlement: totalQuantity,
                metaData: {
                  ...prerequisite.metaData,
                  prerequisiteCatalogue: [...items, newMetaItems]
                }
              }
      },
      [DiscountFieldPaths.PREREQUISITE_CATALOGUE]
    )
  }, [prerequisiteCatalogue, items, setFormState, prerequisite])

  const handleValueChange = useCallback(
    (index: number, value: string, field: 'min' | 'max' = 'min') => {
      const newPrerequisites = [...prerequisiteCatalogue]
      if (newPrerequisites[index]) {
        const existing = newPrerequisites[index]

        let updatedItem = { ...existing }

        const parsed = parseInt(value)
        const parsedValue = isNaN(parsed) ? 0 : parsed
        if (field === 'min') {
          if (existing.type === PrerequisiteType.PRICE) {
            updatedItem = { ...existing, price: parsedValue }
          } else {
            updatedItem = { ...existing, quantity: parsedValue }
          }
        } else {
          if (existing.type === PrerequisiteType.PRICE) {
            updatedItem = { ...existing, maxPrice: parsedValue || undefined }
          } else {
            updatedItem = { ...existing, maxQuantity: parsedValue || undefined }
          }
        }

        newPrerequisites[index] = updatedItem

        const isPrice = existing.type === PrerequisiteType.PRICE
        const fieldKey = field === 'min' ? (isPrice ? 'price' : 'quantity') : isPrice ? 'maxPrice' : 'maxQuantity'

        const totalQuantity = calculateTotalQuantity(newPrerequisites)

        setFormState(
          {
            prerequisite:
              prerequisite.prerequisiteType === PrerequisiteType.PRICE
                ? {
                    ...prerequisite,
                    prerequisiteCatalogue: newPrerequisites
                  }
                : {
                    ...prerequisite,
                    prerequisiteCatalogue: newPrerequisites,
                    prerequisiteCountToEntitlement: totalQuantity
                  }
          },
          [
            `${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${index}.${fieldKey}`,
            ...(field === 'min' ? [`${DiscountFieldPaths.PREREQUISITE_CATALOGUE}.${index}.quantity`] : [])
          ]
        )
      }
    },
    [prerequisiteCatalogue, setFormState, prerequisite]
  )

  return {
    handleItemsSelected,
    handleRemoveGroup,
    handleRemoveItemFromGroup,
    handleRemoveExcludedItem,
    handleVariantUpdate,
    handleAddEmptyGroup,
    handleValueChange
  }
}
